// GraphQL queries and mutations for customer management (Adobe Commerce SaaS)

// Query to get complete customer profile (equivalent to GET /rest/V1/customers/me)
export const CUSTOMER_PROFILE_QUERY = `
  query GetCustomerProfile {
  customer {
    firstname
    lastname
    email
    created_at
    prefix
    middlename
    suffix
    taxvat
    date_of_birth
    gender
    default_billing
    default_shipping
    is_subscribed
    group {
      uid
    }
    addresses {
      id
      firstname
      lastname
      middlename
      prefix
      suffix
      street
      city
      region {
        region_code
        region
        region_id
      }
      postcode
      country_code
      telephone
      fax
      company
      default_billing
      default_shipping
      custom_attributesV2(attributeCodes: null) {
        code
        ... on AttributeValue {
          value
        }
      }
    }
    custom_attributes(attributeCodes: null) {
      code
      ... on AttributeValue {
        value
      }
    }
  }
}
`;

// Mutation to generate customer token
export const GENERATE_CUSTOMER_TOKEN_MUTATION = `
  mutation GenerateCustomerToken($email: String!, $password: String!) {
    generateCustomerToken(email: $email, password: $password) {
      token
    }
  }
`;

// Mutation to change customer password (equivalent to PUT /rest/V1/customers/me/password)
export const CHANGE_CUSTOMER_PASSWORD_MUTATION = `
  mutation ChangeCustomerPassword($currentPassword: String!, $newPassword: String!) {
    changeCustomerPassword(currentPassword: $currentPassword, newPassword: $newPassword) {
      email
    }
  }
`;

// Mutation to update customer profile (equivalent to PUT /rest/V1/customers/me)
export const UPDATE_CUSTOMER_MUTATION = `
  mutation UpdateCustomer($input: CustomerUpdateInput!) {
    updateCustomerV2(input: $input) {
      customer {
        firstname
        lastname
        email
        created_at
        prefix
        middlename
        suffix
        taxvat
        date_of_birth
        gender
        telephone
        default_billing
        default_shipping
        is_subscribed
        addresses {
          id
          firstname
          lastname
          street
          city
          region {
            region_code
            region
            region_id
          }
          postcode
          country_code
          telephone
          company
          default_billing
          default_shipping
        }
        custom_attributes(attributeCodes: null) {
          code
          ... on AttributeValue {
            value
          }
        }
      }
    }
  }
`;

// Mutation to create customer address
export const CREATE_CUSTOMER_ADDRESS_MUTATION = `
  mutation CreateCustomerAddress($input: CustomerAddressInput!) {
    createCustomerAddress(input: $input) {
      id
      firstname
      lastname
      street
      city
      region {
        region_code
        region_id
      }
      postcode
      country_code
      telephone
      company
      default_billing
      default_shipping
    }
  }
`;

// Mutation to update customer address
export const UPDATE_CUSTOMER_ADDRESS_MUTATION = `
  mutation UpdateCustomerAddress($id: Int!, $input: CustomerAddressInput!) {
    updateCustomerAddress(id: $id, input: $input) {
      id
      firstname
      lastname
      street
      city
      region {
        region_code
        region_id
      }
      postcode
      country_code
      telephone
      company
      default_billing
      default_shipping
    }
  }
`;

// Mutation to delete customer address
export const DELETE_CUSTOMER_ADDRESS_MUTATION = `
  mutation DeleteCustomerAddress($id: Int!) {
    deleteCustomerAddress(id: $id)
  }
`;

// Data transformation helper to convert GraphQL customer response to REST format
export const transformCustomerDataToRest = (customer) => {
  if (!customer) return null;

  return {
    id: customer.email,
    email: customer.email,
    firstname: customer.firstname,
    lastname: customer.lastname,
    middlename: customer.middlename,
    prefix: customer.prefix,
    suffix: customer.suffix,
    taxvat: customer.taxvat,
    gender: customer.gender,
    is_subscribed: customer.is_subscribed,
    created_at: customer.created_at,
    date_of_birth: customer.date_of_birth,
    telephone: customer.telephone,
    default_billing: customer.default_billing,
    default_shipping: customer.default_shipping,
    group_id: customer.group?.uid || null,
    custom_attributes: (customer.custom_attributes || []).map(attr => ({
      attribute_code: attr.code,
      value: attr.value ?? null
    })),
    addresses: (customer.addresses || []).map(addr => ({
      id: addr.id,
      firstname: addr.firstname,
      lastname: addr.lastname,
      middlename: addr.middlename,
      prefix: addr.prefix,
      suffix: addr.suffix,
      street: addr.street,
      city: addr.city,
      postcode: addr.postcode,
      country_id: addr.country_code,
      regionId: addr.region?.region_id, // Add regionId at top level for compatibility
      countryId: addr.country_code, // Add countryId for compatibility
      region: {
        region: addr.region?.region,
        region_code: addr.region?.region_code,
        region_id: addr.region?.region_id
      },
      telephone: addr.telephone,
      fax: addr.fax,
      company: addr.company,
      default_billing: addr.default_billing,
      default_shipping: addr.default_shipping,
      custom_attributes: (addr.custom_attributesV2 || []).map(attr => ({
        attribute_code: attr.code,
        value: attr.value ?? null
      })),
      extension_attributes: []
    }))
  };
};

// Query to fetch customer orders with detailed information
export const CUSTOMER_ORDERS_QUERY = `
  query GetCustomerOrders {
    customer {
      orders {
        items {
          id
          number
          order_date
          status
          shipping_method
          carrier
          total {
            grand_total {
              value
              currency
            }
          }
          billing_address {
            firstname
            lastname
            street
            city
            region
            postcode
            country_code
            telephone
            company
          }
          shipping_address {
            firstname
            lastname
            street
            city
            region
            postcode
            country_code
            telephone
            company
          }
          items {
            id
            product_name
            product_sku
            quantity_ordered
            product_sale_price {
              value
              currency
            }
          }
          payment_methods {
            name
            type
          }
        }
      }
    }
  }
`;
// Query to fetch customer orders with detailed information
export const CUSTOMER_ORDERS_WITH_FILTER_QUERY = `
  query GetCustomerOrders(
    $currentPage: Int
    $pageSize: Int
  ) {
    customer {
      orders(
        currentPage: $currentPage
        pageSize: $pageSize
      ) {
        total_count    
        page_info {  
          current_page
          total_pages
          page_size
        }
        items {
          id
          number
          order_date
          status
          shipping_method
          carrier
          total {
            grand_total {
              value
              currency
            }
          }
          billing_address {
            firstname
            lastname
            street
            city
            region
            postcode
            country_code
            telephone
            company
          }
          shipping_address {
            firstname
            lastname
            street
            city
            region
            postcode
            country_code
            telephone
            company
          }
          items {
            id
            product_name
            product_sku
            quantity_ordered
            product_sale_price {
              value
              currency
            }
          }
          payment_methods {
            name
            type
          }
        }
      }
    }
  }
`;

// Transform GraphQL orders data to match Vue component expectations (camelCase)
export const transformOrdersDataToRest = (graphqlData) => {
  if (!graphqlData?.customer?.orders?.items) {
    return { items: [] };
  }

  return {
    totalCount: graphqlData.customer.orders.total_count || 0,
    items: graphqlData.customer.orders.items.map(order => ({
      // Fields expected by the Vue component data table
      entityId: order.id,
      incrementId: order.number,
      createdAt: order.order_date,
      status: order.status,
      baseGrandTotal: order.total?.grand_total?.value || 0,
      total: order.total?.grand_total?.value || 0, // Alternative field name used in template
      customerFirstname: order.billing_address?.firstname || order.shipping_address?.firstname || '',
      customerLastname: order.billing_address?.lastname || order.shipping_address?.lastname || '',
      name: `${order.billing_address?.firstname || order.shipping_address?.firstname || ''} ${order.billing_address?.lastname || order.shipping_address?.lastname || ''}`.trim(),
      items: order.items?.map(item => ({
        productName: item.product_name,
        productSku: item.product_sku,
        qtyOrdered: item.quantity_ordered
      })) || []
    }))
  };
};

// Query to fetch a single customer order by ID
export const CUSTOMER_ORDER_DETAILS_QUERY = `
  query GetCustomerOrderDetails($orderId: ID!) {
    customer {
      orders(filter: { entity_id: { eq: $orderId } }) {
        items {
          id
          number
          order_date
          status
          total {
            grand_total {
              value
              currency
            }
          }
          billing_address {
            firstname
            lastname
            street
            city
            region
            postcode
            country_code
            telephone
            company
          }
          shipping_address {
            firstname
            lastname
            street
            city
            region
            postcode
            country_code
            telephone
            company
          }
          items {
            id
            product_name
            product_sku
            quantity_ordered
            product_sale_price {
              value
              currency
            }
          }
          payment_methods {
            name
            type
          }
        }
      }
    }
  }
`;

// Transform single order GraphQL data to match REST API format for UI compatibility
export const transformSingleOrderDataToRest = (graphqlData, orderId) => {
  const orders = graphqlData?.customer?.orders?.items;
  if (!orders || orders.length === 0) {
    return null;
  }

  const order = orders[0]; // Get the first (and should be only) order

  // Calculate totals from item data since GraphQL doesn't provide detailed breakdown
  const itemTotal = order.items?.reduce((sum, item) => {
    return sum + ((item.product_sale_price?.value || 0) * item.quantity_ordered);
  }, 0) || 0;

  return {
    // Fields expected by the order detail page
    entityId: order.id,
    incrementId: order.number,
    createdAt: order.order_date,
    status: order.status,
    baseGrandTotal: order.total?.grand_total?.value || 0,
    grandTotal: order.total?.grand_total?.value || 0,
    orderCurrencyCode: order.total?.grand_total?.currency || 'USD',

    // Shipping information
    shippingDescription: order.shipping_method || 'Standard Shipping',
    carrier: order.carrier || '',

    // Order totals (using calculated values since GraphQL doesn't provide breakdown)
    baseSubtotalInclTax: itemTotal,
    baseTaxAmount: 0, // Not available in GraphQL response
    baseShippingInclTax: 0, // Not available in GraphQL response

    // Customer info
    customerFirstname: order.billing_address?.firstname || '',
    customerLastname: order.billing_address?.lastname || '',
    customerEmail: '', // Will need to get from customer profile if needed

    // Billing address
    billingAddress: {
      firstname: order.billing_address?.firstname || '',
      lastname: order.billing_address?.lastname || '',
      street: order.billing_address?.street || [],
      city: order.billing_address?.city || '',
      region: order.billing_address?.region || '',
      postcode: order.billing_address?.postcode || '',
      countryId: order.billing_address?.country_code || '',
      telephone: order.billing_address?.telephone || '',
      company: order.billing_address?.company || ''
    },

    // Shipping address (expected format for ViewOrderTabs)
    extensionAttributes: {
      shippingAssignments: [{
        shipping: {
          address: {
            firstname: order.shipping_address?.firstname || '',
            lastname: order.shipping_address?.lastname || '',
            street: order.shipping_address?.street || [],
            city: order.shipping_address?.city || '',
            region: order.shipping_address?.region || '',
            postcode: order.shipping_address?.postcode || '',
            countryId: order.shipping_address?.country_code || '',
            telephone: order.shipping_address?.telephone || '',
            company: order.shipping_address?.company || ''
          }
        }
      }]
    },

    // Items (using field names expected by ViewOrderTabs component)
    items: order.items?.map(item => ({
      itemId: item.id,
      name: item.product_name, // ViewOrderTabs expects 'name' not 'productName'
      sku: item.product_sku,   // ViewOrderTabs expects 'sku' not 'productSku'
      qtyOrdered: item.quantity_ordered,
      basePrice: item.product_sale_price?.value || 0,
      price: item.product_sale_price?.value || 0,
      baseRowTotalInclTax: (item.product_sale_price?.value || 0) * item.quantity_ordered,
      rowTotal: (item.product_sale_price?.value || 0) * item.quantity_ordered
    })) || [],

    // Payment info (format expected by ViewOrderTabs)
    payment: {
      method: order.payment_methods?.[0]?.name || '',
      additionalInformation: [
        order.payment_methods?.[0]?.name || 'No Payment Information', // [0] - Payment method name
        order.payment_methods?.[0]?.type || 'free', // [1] - Payment type
        '', // [2] - Additional info
        '', // [3] - Additional info
        '', // [4] - Additional info
        '', // [5] - Additional info
        '', // [6] - Additional info
        '', // [7] - Used by ViewOrderTabs
        ''  // [8] - Used by ViewOrderTabs
      ]
    }
  };
};
